// React
import PropTypes from "prop-types";

// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Icon,
  Modal,
  Tooltip,
  IconButton,
  Grid,
  MenuItem,
  Select,
  Collapse,
} from "@mui/material";
// Custom UI components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DataTableCustom from "components/Tables/DataTableCustom";
import { useContext, useEffect, useState, useMemo } from "react";
import { MessageManager } from "context";
import { useQuery, gql, useMutation } from "@apollo/client";
import rgba from "assets/theme/functions/rgba";

// Configuraciones del modal
import {
  getColumns,
  getRenderEditCell,
  getActions
} from "./config";
import { getUproColor } from "utils/colors";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  overflowY: "auto",
  p: 3,
  transition: "width 0.5s",
  width: "70%",
};

const APPS = {
  Compras: "SGU",
  Alumnos: "Académica",
  Comedor: "Comedor"
}

export default function ModalPrecioMenu({ open, handleClose }) {
  const { handleSnackbar } = useContext(MessageManager);
  const [editingRow, setEditingRow] = useState(null);
  const [tempPrecio, setTempPrecio] = useState("");
  const [sort, setSort] = useState({ field: null, direction: null });
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState({
    appUsuario: "",
    idSede: "",
    idConvenio: "",
    tipoUsuario: ""
  });

  // Query para obtener opciones de filtros
  const { data: optionsData, loading: optionsLoading } = useQuery(
    gql`
      query optionsFilterMenu {
        optionsFilterMenu {
          apps
          sedes {
            id
            nombre
          }
          convenios {
            id
            nombre
          }
          tiposUser
        }
      }
    `,
    {
      skip: !open,
      fetchPolicy: "cache-first",
    }
  );

  // Query para obtener todos los precios de menú con filtros
  const { data: preciosData, loading: preciosLoading, refetch } = useQuery(
    gql`
      query preciosMenu($filter: JSON) {
        preciosMenu(filter: $filter) {
          id
          idSede
          idCategoria
          idConvenio
          appUsuario
          tipoUsuario
          precio
          sede {
            id
            nombre
          }
          categoria {
            id
            categoria
          }
          convenio {
            id
            nombreInstitucion
          }
        }
      }
    `,
    {
      skip: !open,
      fetchPolicy: "network-only",
      variables: {
        filter: (() => {
          const f = Object.keys(filters).reduce((acc, key) => {
            if (filters[key] && filters[key] !== "") {
              acc[key] = filters[key];
            }
            return acc;
          }, {});
          return Object.keys(f).length > 0 ? f : null;
        })()
      }
    }
  );

  // Mutation para actualizar precio
  const [savePrecioMenu, { loading: saveLoading }] = useMutation(gql`
    mutation savePrecioMenu($input: PreciosMenuInput!) {
      savePrecioMenu(input: $input) {
        id
        precio
      }
    }
  `);

  const handleEditClick = (rowId, currentPrice) => {
    setEditingRow(rowId);
    setTempPrecio(currentPrice || "");
  };

  const handleSaveClick = async (rowId) => {
    try {
      await savePrecioMenu({
        variables: {
          input: {
            id: rowId,
            precio: parseFloat(tempPrecio),
          },
        },
      });
      handleSnackbar("Precio actualizado correctamente", "success");
      setEditingRow(null);
      setTempPrecio("");
      refetch();
    } catch (error) {
      handleSnackbar(error.message, "error");
    }
  };

  const handleCancelClick = () => {
    setEditingRow(null);
    setTempPrecio("");
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...filters,
      [filterName]: value === -1 || value === "" ? "" : value
    };
    setFilters(newFilters);
  };

  const toggleFilters = () => {
    if (openFilters) {
      setOpenFilters(false);
      setFilters({
        appUsuario: "",
        idSede: "",
        idConvenio: "",
        tipoUsuario: ""
      })
    } else {
      setOpenFilters(true);
    }
  };

  // Configuración para usar en DataTableCustom
  const columns = useMemo(() => getColumns(), []);
  const renderEditCell = useMemo(() => getRenderEditCell(tempPrecio, setTempPrecio), [tempPrecio]);
  const actions = useMemo(() => getActions(handleEditClick), []);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <SoftBox display="flex" alignItems="center" gap={2}>
                <SoftButton
                  variant="gradient"
                  color="success"
                  size="small"
                  iconOnly
                  circular
                >
                  <Icon fontSize="medium">restaurant_menu</Icon>
                </SoftButton>
                <SoftTypography variant="h6" fontWeight="bold">
                  Precios Menu
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                <SoftBox mr={2}>
                  <Tooltip title={openFilters ? "Cerrar filtros" : "Ver filtros"} placement="top">
                    <SoftButton color="uproGreen" onClick={toggleFilters} circular>
                      <Icon sx={{ fontWeight: "bold" }}>
                        {openFilters ? "filter_alt_off" : "filter_alt"}
                      </Icon>
                      &nbsp;{openFilters ? "Cerrar filtros" : "Aplicar filtros"}
                    </SoftButton>
                  </Tooltip>
                </SoftBox>
                <Tooltip title="Cerrar" placement="top">
                  <IconButton onClick={handleClose} size="small">
                    <Icon fontSize="medium">close</Icon>
                  </IconButton>
                </Tooltip>
              </SoftBox>
            </SoftBox>

            <Collapse in={openFilters} timeout="auto" unmountOnExit>
              <SoftBox
                display="flex"
                flexDirection={{
                  xs: "column",
                  sm: "row",
                }}
                justifyContent="end"
                alignItems="center"
                px={3}
                pb={3}
                gap={2}
              >
                {/* Filtro por Aplicación */}
                <Select
                  name="apps"
                  value={filters.appUsuario || ""}
                  displayEmpty
                  sx={{
                    paddingRight: "35px !important",
                    minWidth: 150,
                  }}
                  onChange={(e) => handleFilterChange("appUsuario", e.target.value)}
                  disabled={optionsLoading}
                >
                  <MenuItem value="">
                    <SoftBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ width: "100%" }}
                    >
                      <SoftBox>
                        <SoftTypography variant="body2">Todas las Apps</SoftTypography>
                      </SoftBox>
                      <SoftBox>
                        <Icon
                          fontSize="small"
                          sx={{
                            verticalAlign: "middle",
                            color: getUproColor("uproBlue"),
                          }}
                        >
                          apps
                        </Icon>
                      </SoftBox>
                    </SoftBox>
                  </MenuItem>
                  {optionsData?.optionsFilterMenu?.apps?.map((app) => (
                    <MenuItem key={app} value={app}>
                      <SoftTypography variant="body2">{APPS[app]}</SoftTypography>
                    </MenuItem>
                  ))}
                </Select>

                {/* Filtro por Sede */}
                <Select
                  name="sedes"
                  value={filters.idSede || ""}
                  displayEmpty
                  sx={{ minWidth: 150 }}
                  onChange={(e) => handleFilterChange("idSede", e.target.value)}
                  disabled={optionsLoading}
                >
                  <MenuItem value="">
                    <SoftTypography variant="body2">Todas las Sedes</SoftTypography>
                  </MenuItem>
                  {optionsData?.optionsFilterMenu?.sedes?.map((sede) => (
                    <MenuItem key={sede.id} value={sede.id}>
                      <SoftTypography variant="body2">{sede.nombre}</SoftTypography>
                    </MenuItem>
                  ))}
                </Select>

                {/* Filtro por Convenio */}
                <Select
                  name="convenios"
                  value={filters.idConvenio || ""}
                  displayEmpty
                  sx={{ minWidth: 150 }}
                  onChange={(e) => handleFilterChange("idConvenio", e.target.value)}
                  disabled={optionsLoading}
                >
                  <MenuItem value="">
                    <SoftTypography variant="body2">Todos los Convenios</SoftTypography>
                  </MenuItem>
                  {optionsData?.optionsFilterMenu?.convenios?.map((convenio) => (
                    <MenuItem key={convenio.id} value={convenio.id}>
                      <SoftTypography variant="body2">{convenio.nombre}</SoftTypography>
                    </MenuItem>
                  ))}
                </Select>

                {/* Filtro por Tipo de Usuario */}
                <Select
                  name="tipoUsuario"
                  value={filters.tipoUsuario || ""}
                  displayEmpty
                  sx={{ minWidth: 150 }}
                  onChange={(e) => handleFilterChange("tipoUsuario", e.target.value)}
                  disabled={optionsLoading}
                >
                  <MenuItem value="">
                    <SoftTypography variant="body2">Todos los Usuarios</SoftTypography>
                  </MenuItem>
                  {optionsData?.optionsFilterMenu?.tiposUser?.map((tipo) => (
                    <MenuItem key={tipo} value={tipo}>
                      <SoftTypography variant="body2">{tipo}</SoftTypography>
                    </MenuItem>
                  ))}
                </Select>

                {/* Botón para limpiar filtros */}
                <Tooltip title="Limpiar filtros" placement="top">
                  <SoftButton
                    color="uproYellow"
                    size="small"
                    iconOnly
                    onClick={() => setFilters({
                      appUsuario: "",
                      idSede: "",
                      idConvenio: "",
                      tipoUsuario: ""
                    })}
                    disabled={Object.values(filters).every(v => !v)}
                  >
                    <Icon color='dark' fontSize="large">cleaning_services</Icon>
                  </SoftButton>
                </Tooltip>
              </SoftBox>
            </Collapse>

            {/* Contenido del modal */}
            {preciosLoading && !preciosData ? (
              <SoftBox
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                p={5}
                minHeight="300px"
              >
                <CircularProgress size={60} />
                <SoftTypography variant="body2" color="text" mt={2}>
                  Cargando datos...
                </SoftTypography>
              </SoftBox>
            ) : (
              <SoftBox>
                <DataTableCustom
                  headerStyles={{
                    backgroundColor: "#ffffff",             // fondo blanco
                    borderBottom: "1px solid #e91f1fff",
                    py: 1.20,                               // menos alto
                    "& .MuiTypography-root": {
                      fontWeight: 600,                      // negrita media
                      color: "#455A64",                     // gris azulado
                      // textTransform: "none",                // sin uppercase
                      fontSize: "0.8rem",
                      letterSpacing: 0,
                    },
                  }}
                  styleCard={{border: "none"}}
                  columns={columns}
                  data={preciosData?.preciosMenu || []}
                  loading={preciosLoading || optionsLoading}
                  loadingActions={saveLoading}
                  editingRow={editingRow}
                  onEdit={(item) => handleEditClick(item.id, item.precio)}
                  onSave={(item) => handleSaveClick(item.id)}
                  onCancel={handleCancelClick}
                  onDelete={(item) => {
                    // Por ahora no hace nada
                  }}
                  renderEditCell={renderEditCell}
                  actions={actions}

                  // Configuración de ordenamiento
                  sortable={true}
                  defaultSort={sort}
                  onSortChange={setSort}

                  // Configuración de anchos automáticos
                  autoCalculateWidths={true}
                  actionsWidth="15%" // Ancho personalizado para acciones

                  // Configuración de mensajes y apariencia
                  loadingMessage={"Cargando precios..."}
                  emptyMessage={Object.values(filters).some(v => v) ? "No hay resultados con los filtros aplicados" : "No hay precios configurados"}
                  maxHeight="500px"

                  // Footer personalizado
                  footerContent={
                    <SoftBox display="flex" justifyContent="space-between" alignItems="center" width="100%">
                      <SoftBox display="flex" flexDirection="column" alignItems="flex-start">
                        <SoftTypography variant="caption" color="text">
                          Total de items: {preciosData?.preciosMenu?.length || 0}
                        </SoftTypography>
                        {Object.values(filters).some(v => v) && (
                          <SoftTypography variant="caption" color="info" fontWeight="medium">
                            Filtros aplicados: {Object.values(filters).filter(v => v).length}
                          </SoftTypography>
                        )}
                      </SoftBox>
                      <SoftBox display="flex" gap={2}>
                        {sort.field && (
                          <SoftTypography variant="caption" color="text">
                            Ordenado por: {columns.find(col => col.field === sort.field)?.headerName} ({sort.direction === 'asc' ? '↑' : '↓'})
                          </SoftTypography>
                        )}
                        <SoftTypography variant="caption" color="text">
                          {editingRow ? "Editando precio..." : optionsLoading || preciosLoading ? "Cargando..." : "Tabla lista"}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  }
                />
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalPrecioMenu.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};
